// Modèle frontend des employés (Propriétaire / Agence).
// Les permissions effectives suivent le catalogue partagé (modules × actions).

import { EMPLOYEE_ROLE_OPTIONS } from "@/store/permissions/catalog";
import type { PermissionMap } from "@/store/permissions/catalog";

export type {
  PermissionAction,
  PermissionMap,
  PermissionModuleDef,
} from "@/store/permissions/catalog";

export {
  PERMISSION_ACTIONS,
  ACTION_LABELS,
  PERMISSION_MODULES,
  PERMISSION_GROUPS,
  MODULE_LABELS,
  MODULE_ICONS,
  hasPermission,
  countPermissions,
  countModules,
  buildMapFromModules,
  fullAccessMap,
  minimalMap,
  DEFAULT_ROLE_TEMPLATES,
  EMPLOYEE_ROLE_OPTIONS,
  roleTemplate,
  RESERVED_ROLES,
} from "@/store/permissions/catalog";

export type EmployeStatut = "actif" | "inactif" | "invite";

/** Clé de permission au format "module.action" (ex. "biens.voir"). */
export type PermissionKey = string;

export interface Employe {
  id: string;
  prenom: string;
  nom: string;
  email: string;
  telephone: string;
  /** Code du rôle employé (persisté dans employes_agence.fonction). */
  role: string;
  statut: EmployeStatut;
  permissions: PermissionMap;
  createdAt: string;
}

export type EmployeInput = Omit<Employe, "id" | "createdAt">;

export const EMPLOYE_STATUT_LABELS: Record<EmployeStatut, string> = {
  actif: "Actif",
  inactif: "Inactif",
  invite: "Invité",
};

export const EMPLOYE_STATUT_ICONS: Record<EmployeStatut, string> = {
  actif: "ri-checkbox-circle-line",
  inactif: "ri-pause-circle-line",
  invite: "ri-mail-send-line",
};

export const EMPLOYE_STATUT_TONES: Record<EmployeStatut, "secondary" | "neutral" | "accent"> = {
  actif: "secondary",
  inactif: "neutral",
  invite: "accent",
};

export const EMPLOYE_STATUT_OPTIONS = (Object.keys(EMPLOYE_STATUT_LABELS) as EmployeStatut[]).map((value) => ({
  value,
  label: EMPLOYE_STATUT_LABELS[value],
}));

/** Libellé d'un rôle employé à partir de son code. */
export function employeRoleLabel(role: string): string {
  const fromCatalog = (EMPLOYEE_ROLE_OPTIONS as { value: string; label: string }[]).find(
    (option) => option.value === role
  )?.label;
  return fromCatalog ?? (role || "Rôle non défini");
}

export function employeNomComplet(employe: Pick<Employe, "prenom" | "nom">): string {
  return [employe.prenom, employe.nom].filter(Boolean).join(" ").trim() || "Employé sans nom";
}

export function employeInitiales(employe: Pick<Employe, "prenom" | "nom">): string {
  const prenom = employe.prenom.trim().charAt(0);
  const nom = employe.nom.trim().charAt(0);
  return `${prenom}${nom}`.toUpperCase() || "E";
}